import { queryOptions } from '@tanstack/react-query';
import type { HevitonApiV2, RawPlantNode } from '@heviton/heviton-api-v2';
import { fromRawSubscriptionListItem } from '@/shared/data-mappers';
import type { SubscriptionListItem } from '@/shared/data-types';

/**
 * 단건 발전소 query 정의 SSOT. BE = v2 `GET /plants/{plantId}?include=subscription,order` (`plant.detail`).
 * 구독유형 선택(plan) 페이지가 진입 발전소 1건을 **목록 페이지네이션과 무관하게** 직접 조회 — 딥링크·새로고침 안전.
 * 반환 = `SubscriptionListItem`(발전소 기본 + 구독·최신주문 합성) — plan.guard 의 구독중 차단·결제 게이팅에 재사용.
 *
 * mock/real 전환은 `USE_MOCK` flag. 결정/미결: `docs/subscription-plan-decisions.md`.
 */
export const plantQueryKey = (plantId: string) => ['plant', plantId] as const;

/** mock 토글 — true=mock, false=실제 v2 `plant.detail`. 실연동 완료 시 flag·mock 제거. [TODO:mock] */
const USE_MOCK = false;

// mock 발전소 — 구독 목록 mock 과 동일 ID·이름(offline 일관성). entity↔entity import 금지라 자체 보유. [TODO:mock]
function mockPlant(
  plantId: string,
  plantName: string,
  location: string,
  volume: number,
): RawPlantNode {
  return {
    plantId,
    plantName,
    customerId: 'cust-001',
    isPublic: false,
    isRepresentative: false,
    companyId: 1,
    energy: '501',
    location,
    processStatus: '',
    checkStatus: '',
    facilitiesStatus: '',
    volume,
  };
}

const MOCK_PLANTS: RawPlantNode[] = [
  mockPlant('plant-001', '서울식물원주차장태양광발전소', '서울시 강서구', 500),
  mockPlant('plant-002', '헤리트태양광발전소01', '경기도 성남시 분당구', 300),
  mockPlant('plant-003', '헤리트태양광발전소02', '경기도 성남시 분당구', 200),
  mockPlant('plant-004', '강남옥상태양광발전소', '서울시 강남구', 150),
  mockPlant('plant-005', '판교제2태양광발전소', '경기도 성남시 분당구', 1500),
  mockPlant('plant-006', '세종행정타운태양광발전소', '세종시', 450),
  mockPlant('plant-007', '대전유성구청사옥상태양광', '대전시 유성구', 95),
  mockPlant('plant-008', '광교호수공원태양광발전소', '경기도 수원시 영통구', 250),
  mockPlant('plant-009', '동탄제3태양광발전소', '경기도 화성시', 320),
  mockPlant('plant-010', '청주산업단지태양광', '충북 청주시', 800),
  mockPlant('plant-011', '여수산업단지태양광', '전남 여수시', 1200),
];

export const plantQueryOptions = (api: HevitonApiV2, plantId: string) =>
  queryOptions<SubscriptionListItem>({
    queryKey: plantQueryKey(plantId),
    enabled: !!plantId,
    queryFn: async ({ signal }) => {
      if (USE_MOCK) {
        await new Promise((resolve) => setTimeout(resolve, 300)); // mock latency
        const found = MOCK_PLANTS.find((p) => p.plantId === plantId);
        if (!found) throw new Error(`mock: plant not found (${plantId})`); // 실 API 404 와 동치
        return fromRawSubscriptionListItem(found);
      }
      const raw = await api.plant.detail(
        Number(plantId),
        { include: 'subscription,order' },
        { signal },
      );
      return fromRawSubscriptionListItem(raw.plant);
    },
  });
